/* ============================================
   Portal Dashboard — Strategic Control Center 360°
   KPIs, project health, hours, investment, alerts
   ============================================ */

import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { usePortalCliente } from '@/hooks/usePortalCliente'
import { useCachedData } from '@/hooks/useCachedData'
import { useAuth } from '@/hooks/useAuth'
import { useApi } from '@/hooks/useApi'
import { supabase } from '@/lib/supabase'
import { formatCurrency, formatDate } from '@/lib/utils'
import type { Tarea, Proyecto, ConfigTarifaCliente, Cotizacion, Factura, MiApp, PortalAppsResponse } from '@/lib/types'
import { puedeVerFacturacion } from '@/lib/portal-permisos'
import { Breadcrumbs } from '@/components/ui'

// Saldo de créditos del cliente en el gateway (una cuenta para toda la plataforma).
type ConsumoResumen = {
  disponible: boolean
  balance?: { balance?: number; unidad?: string; umbral_aviso?: number; saldo_bajo?: boolean }
}

type HostingItem = {
  id: number; plan_nombre: string; dominio: string | null; estado: string
  fecha_vencimiento: string | null; periodo: string
}

// Config visual del estado de un hosting (compacto, para el dashboard).
const hostingEstado: Record<string, { label: string; color: string; bg: string }> = {
  activo: { label: 'Activo', color: 'var(--success)', bg: 'color-mix(in srgb, var(--success) 12%, transparent)' },
  pagado: { label: 'En proceso', color: 'var(--warning)', bg: 'color-mix(in srgb, var(--warning) 12%, transparent)' },
  suspendido: { label: 'Suspendido', color: 'var(--danger)', bg: 'color-mix(in srgb, var(--danger) 12%, transparent)' },
}

// ─── SVG Icons (inline, 20px stroke-based) ───
function IconApps() {
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /></svg>
}

function IconTasks() {
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" /></svg>
}
function IconCheck() {
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
}
function IconClock() {
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></svg>
}
function IconInbox() {
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 16 12 14 15 10 15 8 12 2 12" /><path d="M5.45 5.11L2 12v6a2 2 0 002 2h16a2 2 0 002-2v-6l-3.45-6.89A2 2 0 0016.76 4H7.24a2 2 0 00-1.79 1.11z" /></svg>
}
function IconAlert() {
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
}
function IconDollar() {
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" /></svg>
}
function IconTimer() {
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
}
function IconActivity() {
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg>
}

export default function PortalDashboard() {
  const { activeClienteId: clienteId, afiliaciones } = usePortalCliente()
  const { user, contactoId } = useAuth()
  // La tarjeta de facturación es de PRINCIPAL / FACTURACIÓN. El resto de roles
  // (TÉCNICO, COMERCIAL, GERENCIA) ve el resumen operativo sin importes.
  const rolesActivos: string[] =
    (afiliaciones.find(a => a.cliente_id === clienteId)?.roles as string[] | undefined) ?? user?.roles ?? []
  const verFacturacion = puedeVerFacturacion(rolesActivos, contactoId)
  const api = useApi()
  // El nombre real vive en la ficha del cliente (user_metadata no existe en este
  // auth). Lo traemos del perfil; mientras carga, caemos al prefijo del email.
  // En cache aparte del tablero: no depende de la empresa activa, asi que
  // cambiar de cuenta no tiene por que volver a pedirlo.
  const { data: nombrePerfil } = useCachedData(['portal-perfil-nombre'], () =>
    api.get<{ nombre?: string }>('/api/portal/perfil').then(p => p?.nombre ?? '').catch(() => ''))
  const primerNombre = String(nombrePerfil || user?.email || '').trim().split(/[\s@]/)[0]

  // Todo el tablero en una sola consulta cacheada. La carga es la de antes
  // —encadenada: los proyectos deciden que tareas pedir, y las apps si vale la
  // pena preguntar el consumo— asi que no se reescribio, se envolvio: lo unico
  // que cambia es que al volver a Resumen no se pide de nuevo.
  const { data: tablero, isLoading: loading } = useCachedData(
    ['portal-dashboard', clienteId],
    async () => {
      const { data: pData } = await supabase.from('proyectos').select('*').eq('cliente_id', clienteId).eq('estado', 'activo')
      const proyectos = (pData ?? []) as unknown as Proyecto[]
      const proyIds = proyectos.map(p => p.id)

      const [{ data: tData }, { data: cfData }] = await Promise.all([
        proyIds.length > 0
          ? supabase.from('tareas').select('*').in('proyecto_id', proyIds)
          : Promise.resolve({ data: [] }),
        supabase.from('config_tarifa_cliente').select('*').eq('cliente_id', clienteId).limit(1),
      ])

      // Cotizaciones y facturas para los KPIs financieros.
      const [{ data: cotData }, { data: facData }] = await Promise.all([
        supabase.from('cotizaciones').select('*').eq('cliente_id', clienteId).neq('estado', 'borrador').order('fecha_emision', { ascending: false }),
        supabase.from('facturas').select('*').eq('cliente_id', clienteId).neq('estado', 'borrador').order('fecha_emision', { ascending: false }),
      ])

      // Hosting del cliente (para el modulo de hosting del dashboard).
      const hostings = await api.get<HostingItem[]>(`/api/hosting/mi-hosting?cliente_id=${clienteId}`)
        .then(h => (Array.isArray(h) ? h : []))
        .catch(() => [] as HostingItem[])

      // Apps activadas + saldo de creditos. El saldo vive en el gateway, asi
      // que degrada a null si la reporteria no esta disponible.
      let misApps: MiApp[] = []
      let consumo: ConsumoResumen | null = null
      try {
        const apps = await api.get<PortalAppsResponse>(`/api/portal/apps?cliente_id=${clienteId}`)
        misApps = (apps?.mis_apps ?? []).filter(a => a.estado !== 'revocada')
        if (misApps.length > 0) {
          consumo = await api.get<ConsumoResumen>(`/api/portal/apps/${misApps[0].app_slug}/consumo`).catch(() => null)
        }
      } catch { misApps = [] }

      return {
        proyectos,
        tareas: (tData ?? []) as unknown as Tarea[],
        tarifa: (cfData && (cfData as unknown[]).length > 0 ? (cfData as unknown[])[0] : null) as ConfigTarifaCliente | null,
        cotizaciones: (cotData ?? []) as unknown as Cotizacion[],
        facturas: (facData ?? []) as unknown as Factura[],
        hostings,
        misApps,
        consumo,
      }
    },
    { activo: !!clienteId },
  )
  const proyectos = tablero?.proyectos ?? []
  const tareas = tablero?.tareas ?? []
  const tarifa = tablero?.tarifa ?? null
  const cotizaciones = tablero?.cotizaciones ?? []
  const facturas = tablero?.facturas ?? []
  const hostings = tablero?.hostings ?? []
  const misApps = tablero?.misApps ?? []
  const consumo = tablero?.consumo ?? null

  // ─── Computed metrics ───
  const metrics = useMemo(() => {
    const total = tareas.length
    const completadas = tareas.filter(t => t.estado === 'completada').length
    const enProgreso = tareas.filter(t => t.estado === 'en_progreso').length
    const pendientes = tareas.filter(t => t.estado === 'pendiente').length
    const bloqueadas = tareas.filter(t => t.estado === 'bloqueada').length
    const horasUsadas = tareas
      .filter(t => t.tipo_cobro === 'por_horas')
      .reduce((sum, t) => sum + (t.horas_estimadas || 0), 0)
    const montoTotal = tareas
      .filter(t => t.monto && t.monto > 0)
      .reduce((sum, t) => sum + (t.monto || 0), 0)
    return { total, completadas, enProgreso, pendientes, bloqueadas, horasUsadas, montoTotal }
  }, [tareas])

  // Financial metrics (cotizaciones + facturas)
  const financialMetrics = useMemo(() => {
    const cotPendientes = cotizaciones.filter(c => c.estado === 'enviada')
    const cotAceptadas = cotizaciones.filter(c => c.estado === 'aceptada')
    const facPendientes = facturas.filter(f => ['emitida', 'pre_factura'].includes(f.estado))
    const facVencidas = facPendientes.filter(f => f.fecha_vencimiento && new Date(f.fecha_vencimiento) < new Date())
    const facPagadas = facturas.filter(f => f.estado === 'pagada')
    return {
      cotPendientesCount: cotPendientes.length,
      cotPendientesTotal: cotPendientes.reduce((s, c) => s + c.total, 0),
      cotAceptadasCount: cotAceptadas.length,
      facPendientesCount: facPendientes.length,
      facPendientesTotal: facPendientes.reduce((s, f) => s + f.total, 0),
      facVencidasCount: facVencidas.length,
      facPagadasTotal: facPagadas.reduce((s, f) => s + f.total, 0),
    }
  }, [cotizaciones, facturas])

  // Recent completed tasks (last 5)
  const recentCompleted = useMemo(() => {
    return tareas
      .filter(t => t.estado === 'completada')
      .sort((a, b) => (b.fecha_completada || b.created_at || '').localeCompare(a.fecha_completada || a.created_at || ''))
      .slice(0, 5)
  }, [tareas])

  // Blocked tasks
  const tareasBlockeadas = useMemo(() => {
    return tareas.filter(t => t.estado === 'bloqueada')
  }, [tareas])

  const umbral = tarifa?.umbral_horas_mes ?? 0
  const horasPercent = umbral > 0 ? Math.min(100, (metrics.horasUsadas / umbral) * 100) : 0
  const moneda = tarifa?.moneda || 'USD'

  // Project health semaphore
  function getProjectHealth(proyecto: Proyecto, projectTareas: Tarea[]): { color: string; label: string } {
    const done = projectTareas.filter(t => t.estado === 'completada').length
    const total = projectTareas.length
    const pct = total > 0 ? done / total : 0
    const blocked = projectTareas.some(t => t.estado === 'bloqueada')
    
    if (blocked) return { color: 'var(--danger)', label: 'Bloqueado' }
    
    if (proyecto.fecha_fin_estimada) {
      const endDate = new Date(proyecto.fecha_fin_estimada)
      const now = new Date()
      const totalDays = (endDate.getTime() - new Date(proyecto.fecha_inicio || proyecto.created_at).getTime()) / (1000 * 60 * 60 * 24)
      const elapsedDays = (now.getTime() - new Date(proyecto.fecha_inicio || proyecto.created_at).getTime()) / (1000 * 60 * 60 * 24)
      const timeProgress = totalDays > 0 ? elapsedDays / totalDays : 0
      
      if (now > endDate && pct < 1) return { color: 'var(--danger)', label: 'Retrasado' }
      if (timeProgress > 0.8 && pct < 0.7) return { color: 'var(--warning)', label: 'En riesgo' }
    }
    
    return { color: 'var(--success)', label: 'En tiempo' }
  }

  // El portal se diseñó para clientes de agencia (tareas/horas/inversión). Un cliente solo
  // de hosting no tiene nada de eso → mostramos las métricas de agencia SOLO si hay proyectos,
  // y un módulo de hosting propio si tiene hosting. Dashboard modular por servicio.
  const hasAgencia = proyectos.length > 0
  // Moneda real del cliente para los KPIs (sus facturas). Evita el default USD:
  // los pagos de hosting son en PEN y se mostraban como "5.00 USD".
  const monedaCliente = facturas.find(f => f.moneda)?.moneda || 'PEN'

  if (loading) {
    return (
      <div className="portal-loading-state">
        <div className="auth-spinner" style={{ margin: '0 auto 12px' }} />
        <p>Cargando dashboard...</p>
      </div>
    )
  }

  return (
    <div className="pd">
      {/* ═══ Header de bienvenida — personal + CTAs (inspirado en el patrón de hosting) ═══ */}
      <Breadcrumbs items={[{ label: 'Portal', to: '/portal' }, { label: 'Resumen' }]} />
      <div style={{ marginBottom: 20 }}>
        <span className="portal-saludo">Hola{primerNombre ? `, ${primerNombre}` : ''} 👋</span>
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', margin: 0 }}>Este es el resumen de tus servicios con 1Conecta.</p>
      </div>

      {/* ═══ Contadores por servicio — solo los que aplican al cliente ═══ */}
      {(() => {
        const counters = [
          { label: 'Hosting', value: hostings.length, show: hostings.length > 0 },
          { label: 'Comprobantes', value: facturas.length, show: verFacturacion && facturas.length > 0 },
          { label: 'Proyectos', value: proyectos.length, show: hasAgencia },
        ].filter(c => c.show)
        if (counters.length === 0) return null
        return (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 20 }}>
            {counters.map(c => (
              <div key={c.label} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: '14px 16px' }}>
                <div style={{ fontSize: '1.6rem', fontWeight: 700, lineHeight: 1 }}>{c.value}</div>
                <div style={{ fontSize: '0.68rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em', marginTop: 5 }}>{c.label}</div>
              </div>
            ))}
          </div>
        )
      })()}

      {/* ═══ Módulo Hosting — solo si el cliente tiene hosting ═══ */}
      {hostings.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <span style={{ fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.04em', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Mi hosting</span>
            <Link to="/portal/hosting" style={{ fontSize: '0.78rem', color: 'var(--info)', textDecoration: 'none' }}>Ver todo →</Link>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12 }}>
            {hostings.map(h => {
              const cfg = hostingEstado[h.estado] || hostingEstado.activo
              const dias = h.fecha_vencimiento ? Math.ceil((new Date(h.fecha_vencimiento).getTime() - Date.now()) / 86400000) : null
              const vColor = dias == null ? 'var(--text-secondary)' : dias < 0 ? '#d63031' : dias <= 15 ? '#fdcb6e' : 'var(--text-secondary)'
              return (
                <Link key={h.id} to="/portal/hosting" style={{
                  display: 'block', textDecoration: 'none', color: 'inherit',
                  background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14, padding: '14px 16px',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 10 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: '0.92rem' }}>{h.plan_nombre}</div>
                      <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h.dominio || 'Sin dominio'}</div>
                    </div>
                    <span style={{ flexShrink: 0, fontSize: '0.68rem', fontWeight: 600, padding: '3px 9px', borderRadius: 20, color: cfg.color, background: cfg.bg }}>{cfg.label}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.74rem' }}>
                    <span style={{ color: 'var(--text-secondary)', textTransform: 'capitalize' }}>{h.periodo}</span>
                    {h.fecha_vencimiento && (
                      <span style={{ color: vColor }}>
                        Vence {formatDate(h.fecha_vencimiento)}{dias != null && dias >= 0 ? ` · ${dias}d` : dias != null ? ' · vencido' : ''}
                      </span>
                    )}
                  </div>
                </Link>
              )
            })}
          </div>
        </div>
      )}

      {/* ═══ Facturación — solo PRINCIPAL / FACTURACIÓN (moneda del cliente) ═══ */}
      {verFacturacion && (
      <div style={{ marginBottom: 16 }}>
        <div className={`pd-card ${financialMetrics.facVencidasCount > 0 ? 'pd-card--alert' : ''}`}>
          <div className="pd-card-header">
            <div className="pd-card-title"><IconDollar /> Facturación</div>
            {financialMetrics.facPendientesCount > 0 && (
              <span className="pd-alert-badge" style={financialMetrics.facVencidasCount > 0 ? { background: 'var(--danger)' } : {}}>{financialMetrics.facPendientesCount}</span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 40, flexWrap: 'wrap', padding: '12px 0 4px' }}>
            <div>
              <div style={{ fontSize: '1.4rem', fontWeight: 700, color: financialMetrics.facPagadasTotal > 0 ? 'var(--success)' : 'var(--text-tertiary)' }}>{formatCurrency(financialMetrics.facPagadasTotal, monedaCliente)}</div>
              <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', marginTop: 2 }}>Total pagado</div>
            </div>
            <div>
              <div style={{ fontSize: '1.4rem', fontWeight: 700, color: financialMetrics.facPendientesTotal > 0 ? 'var(--warning)' : 'var(--text-tertiary)' }}>{formatCurrency(financialMetrics.facPendientesTotal, monedaCliente)}</div>
              <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', marginTop: 2 }}>
                Pendiente de pago{financialMetrics.facVencidasCount > 0 ? ` · ${financialMetrics.facVencidasCount} vencida${financialMetrics.facVencidasCount > 1 ? 's' : ''}` : ''}
              </div>
            </div>
          </div>
        </div>
      </div>
      )}

      {hasAgencia && (<>
      {/* ═══ Row 1: KPI Cards ═══ */}
      <div className="pd-kpis">
        <div className="pd-kpi">
          <div className="pd-kpi-icon"><IconTasks /></div>
          <div className="pd-kpi-data">
            <span className="pd-kpi-value">{metrics.total}</span>
            <span className="pd-kpi-label">Tareas Totales</span>
          </div>
        </div>
        <div className="pd-kpi pd-kpi--success">
          <div className="pd-kpi-icon"><IconCheck /></div>
          <div className="pd-kpi-data">
            <span className="pd-kpi-value">{metrics.completadas}</span>
            <span className="pd-kpi-label">Completadas</span>
          </div>
        </div>
        <div className="pd-kpi pd-kpi--info">
          <div className="pd-kpi-icon"><IconClock /></div>
          <div className="pd-kpi-data">
            <span className="pd-kpi-value">{metrics.enProgreso}</span>
            <span className="pd-kpi-label">En Progreso</span>
          </div>
        </div>
        <div className="pd-kpi pd-kpi--warning">
          <div className="pd-kpi-icon"><IconInbox /></div>
          <div className="pd-kpi-data">
            <span className="pd-kpi-value">{metrics.pendientes}</span>
            <span className="pd-kpi-label">Pendientes</span>
          </div>
        </div>
        <div className={`pd-kpi pd-kpi--danger ${metrics.bloqueadas > 0 ? 'pd-kpi--pulse' : ''}`}>
          <div className="pd-kpi-icon"><IconAlert /></div>
          <div className="pd-kpi-data">
            <span className="pd-kpi-value">{metrics.bloqueadas}</span>
            <span className="pd-kpi-label">Bloqueadas</span>
          </div>
        </div>
      </div>

      {/* ═══ Row 2: Hours + Investment (2-col grid) ═══ */}
      <div className="pd-grid-2">
        {/* Hours Card */}
        <div className="pd-card">
          <div className="pd-card-header">
            <div className="pd-card-title"><IconTimer /> Horas Consumidas</div>
            <span className="pd-card-detail">
              {metrics.horasUsadas}h{umbral > 0 ? ` / ${umbral}h` : ''}
            </span>
          </div>
          {umbral > 0 ? (
            <>
              <div className="pd-bar-track">
                <div
                  className={`pd-bar-fill ${horasPercent >= 100 ? 'critical' : horasPercent >= 80 ? 'warning' : ''}`}
                  style={{ width: `${horasPercent}%` }}
                />
              </div>
              <div className="pd-card-footer">
                {umbral - metrics.horasUsadas > 0
                  ? <span>{Math.round(umbral - metrics.horasUsadas)}h disponibles</span>
                  : <span className="pd-text-danger">Límite alcanzado</span>}
                <span className="pd-text-muted">{Math.round(horasPercent)}% usado</span>
              </div>
            </>
          ) : (
            <div className="pd-card-footer">
              <span className="pd-text-muted">{metrics.horasUsadas}h registradas · Sin límite configurado</span>
            </div>
          )}
        </div>

        {/* Investment Card */}
        <div className="pd-card">
          <div className="pd-card-header">
            <div className="pd-card-title"><IconDollar /> Inversión Total</div>
          </div>
          <div className="pd-investment-amount">{formatCurrency(metrics.montoTotal, moneda)}</div>
          <div className="pd-card-footer">
            <span className="pd-text-muted">{metrics.completadas} tareas completadas</span>
            {tarifa && <span className="pd-text-muted">Tarifa: {formatCurrency(tarifa.tarifa_hora, moneda)}/h</span>}
          </div>
          {/* Budget progress if available */}
          {proyectos.some(p => p.presupuesto > 0) && (() => {
            const totalBudget = proyectos.reduce((s, p) => s + (p.presupuesto || 0), 0)
            const budgetPct = totalBudget > 0 ? Math.min(100, (metrics.montoTotal / totalBudget) * 100) : 0
            return (
              <>
                <div className="pd-bar-track" style={{ marginTop: 8 }}>
                  <div
                    className={`pd-bar-fill ${budgetPct >= 100 ? 'critical' : budgetPct >= 80 ? 'warning' : ''}`}
                    style={{ width: `${budgetPct}%`, background: budgetPct >= 80 ? '' : 'linear-gradient(90deg, #00d2d3, #0abde3)' }}
                  />
                </div>
                <div className="pd-card-footer" style={{ marginTop: 4 }}>
                  <span className="pd-text-muted">{Math.round(budgetPct)}% del presupuesto ({formatCurrency(totalBudget, moneda)})</span>
                </div>
              </>
            )
          })()}
        </div>
      </div>

      {/* ═══ Row 3: Projects Health ═══ */}
      {proyectos.length > 0 && (
        <div className="pd-card">
          <div className="pd-card-header">
            <div className="pd-card-title">Proyectos Activos</div>
            <span className="pd-card-detail">{proyectos.length} proyecto{proyectos.length > 1 ? 's' : ''}</span>
          </div>
          <div className="pd-projects">
            {proyectos.map(p => {
              const pTareas = tareas.filter(t => t.proyecto_id === p.id)
              const done = pTareas.filter(t => t.estado === 'completada').length
              const total = pTareas.length
              const pct = total > 0 ? Math.round((done / total) * 100) : 0
              const health = getProjectHealth(p, pTareas)
              return (
                <div key={p.id} className="pd-project">
                  <div className="pd-project-header">
                    <div className="pd-project-name">
                      <span className="pd-health-dot" style={{ background: health.color }} title={health.label} />
                      {p.nombre}
                    </div>
                    <span className="pd-project-stats">{done}/{total} · {pct}%</span>
                  </div>
                  <div className="pd-bar-track">
                    <div className="pd-bar-fill" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* ═══ Row 4: Activity + Attention Required (2-col) ═══ */}
      <div className="pd-grid-2">
        {/* Recent Activity */}
        <div className="pd-card">
          <div className="pd-card-header">
            <div className="pd-card-title"><IconActivity /> Actividad Reciente</div>
          </div>
          {recentCompleted.length > 0 ? (
            <div className="pd-activity-list">
              {recentCompleted.map(t => (
                <div key={t.id} className="pd-activity-item">
                  <div className="pd-activity-dot" />
                  <div className="pd-activity-content">
                    <span className="pd-activity-title">{t.titulo}</span>
                    <span className="pd-activity-meta">
                      Completada {t.fecha_completada ? formatDate(t.fecha_completada) : ''}
                      {t.monto ? ` · ${formatCurrency(t.monto, moneda)}` : ''}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="pd-empty-mini">Sin actividad reciente</div>
          )}
        </div>

        {/* Attention Required */}
        <div className={`pd-card ${tareasBlockeadas.length > 0 ? 'pd-card--alert' : ''}`}>
          <div className="pd-card-header">
            <div className="pd-card-title">
              <IconAlert /> Atención Requerida
            </div>
            {tareasBlockeadas.length > 0 && (
              <span className="pd-alert-badge">{tareasBlockeadas.length}</span>
            )}
          </div>
          {tareasBlockeadas.length > 0 ? (
            <div className="pd-blocked-list">
              {tareasBlockeadas.map(t => {
                const proj = proyectos.find(p => p.id === t.proyecto_id)
                return (
                  <div key={t.id} className="pd-blocked-item">
                    <div className="pd-blocked-dot" />
                    <div className="pd-blocked-content">
                      <span className="pd-blocked-title">{t.titulo}</span>
                      {proj && <span className="pd-blocked-project">{proj.nombre}</span>}
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <div className="pd-empty-mini pd-empty-mini--success">
              <IconCheck />
              <span>Sin bloqueos. Todo avanza correctamente.</span>
            </div>
          )}
        </div>
      </div>

      </>)}

      {/* ═══ Apps activas + saldo de créditos ═══ */}
      {misApps.length > 0 && (
        <div className="pd-card">
          <div className="pd-card-header">
            <span className="pd-card-title"><IconApps />TUS APPS</span>
            <Link to="/portal/apps" className="pd-card-detail">Ver todas →</Link>
          </div>
          <div className="pd-apps-list">
            {misApps.map(a => (
              <Link key={a.id} to={`/portal/apps/${a.app_slug}`} className="pd-app-row">
                {a.icono_url
                  ? <img className="pa-logo pa-logo--img pd-app-logo" src={a.icono_url} alt={a.nombre ?? a.app_slug} />
                  : <span className="pa-logo pd-app-logo">{(a.nombre ?? a.app_slug).charAt(0).toUpperCase()}</span>}
                <span className="pd-app-name">{a.nombre ?? a.app_slug}</span>
                <span className={`badge ${a.estado === 'activada' ? 'badge-activo' : 'badge-pausado'}`}>
                  {a.estado === 'activada' ? 'Activa' : 'Pendiente'}
                </span>
              </Link>
            ))}
          </div>
          {consumo?.disponible && (
            <>
              <div className="pd-card-footer">
                <span className="pd-text-muted">Saldo de créditos</span>
                <strong>
                  {(consumo.balance?.balance ?? 0).toLocaleString('es-PE', { maximumFractionDigits: 2 })}
                  {' '}{consumo.balance?.unidad || 'créditos'}
                </strong>
              </div>
              {(consumo.balance?.saldo_bajo ?? (consumo.balance?.balance ?? 0) < 200) && (
                <div className="pad-alerta" style={{ marginTop: 10 }}>
                  Te quedan pocos créditos. Al llegar a cero, las apps que cobran por uso dejan de responder.
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ═══ Empty state — solo si no tiene NI hosting NI proyectos NI apps ═══ */}
      {hostings.length === 0 && proyectos.length === 0 && misApps.length === 0 && (
        <div className="pd-card" style={{ textAlign: 'center', padding: '2.5rem 3rem' }}>
          <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="1.5">
            <rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18" /><path d="M9 21V9" />
          </svg>
          <h3 style={{ marginTop: 14, color: 'var(--text-secondary)' }}>Aún no hay nada que mostrar</h3>
          <p style={{ color: 'var(--text-tertiary)' }}>Cuando actives un servicio con nosotros, verás tu resumen aquí.</p>
        </div>
      )}
    </div>
  )
}
